<script setup>
import { track } from '../analytics'
import { ref, onMounted } from 'vue'

const email = ref('')
const name = ref('')
const company = ref('')
const loading = ref(false)
const success = ref(false)
const error = ref('')
const source = ref('')

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.krate.sh'

onMounted(() => {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams(window.location.search)
  const utm = params.get('utm_source') || params.get('utm_campaign')
  const ref_ = document.referrer && !document.referrer.includes(window.location.host)
    ? new URL(document.referrer).hostname
    : null
  // ?email=… arrives when the auth flow bounces the user here from a GitHub
  // identity that has no approved signup — prefill so the form reflects what
  // they tried to sign in with.
  const prefill = params.get('email')
  if (prefill) email.value = prefill
  source.value = utm || (params.get('signup') ? 'gated-login' : ref_ || 'direct')
})

const submit = async (e) => {
  e.preventDefault()
  loading.value = true
  error.value = ''

  try {
    const response = await fetch(`${API_BASE}/api/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.value.trim(),
        name: name.value.trim() || undefined,
        company: company.value.trim() || undefined,
        source: source.value || undefined,
      }),
    })

    if (response.ok) {
      // The real conversion. Tracked here rather than with a data-umami-event
      // on the submit button, which would also fire on validation errors,
      // duplicate submits, and server failures.
      track('waitlist-signup', { source: source.value || 'direct' })
      success.value = true
      email.value = ''
      name.value = ''
      company.value = ''
    } else {
      const text = await response.text().catch(() => '')
      error.value = text || `Server returned ${response.status}. Please try again.`
    }
  } catch (err) {
    error.value = 'Network error. Please try again.'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <section id="waitlist" class="py-20 px-4" style="background: linear-gradient(135deg, var(--color-krate-blue-600), var(--color-krate-blue-800));">
    <div class="max-w-2xl mx-auto text-center">
      <h2 class="text-4xl md:text-5xl font-bold text-white mb-4">Join the beta</h2>
      <p class="text-xl mb-8" style="color: var(--color-krate-blue-100);">
        We're inviting people in waves while we work through the beta. Drop your email and we'll be in touch.
      </p>

      <div v-if="success" class="rounded-lg p-6 text-white text-left mb-4" style="background-color: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2);">
        <p class="font-semibold mb-1" style="color: #facc15;">Thanks! You're on the list.</p>
        <p class="text-sm" style="color: var(--color-krate-blue-100);">
          Check your inbox for a confirmation email. We'll send another note when your beta application is approved.
        </p>
      </div>

      <form v-else @submit="submit" class="space-y-3 text-left">
        <input v-model="email" type="email" required placeholder="you@example.com" class="waitlist-input w-full px-5 py-3 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-white" />
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input v-model="name" type="text" placeholder="Your name (optional)" class="waitlist-input w-full px-5 py-3 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-white" />
          <input v-model="company" type="text" placeholder="Company (optional)" class="waitlist-input w-full px-5 py-3 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-white" />
        </div>
        <button type="submit" :disabled="loading" class="w-full font-semibold px-8 py-4 rounded-lg transition-opacity disabled:opacity-50" style="background-color: white; color: var(--color-krate-blue-600);">
          {{ loading ? 'Submitting…' : 'Join the beta' }}
        </button>
      </form>

      <p v-if="error" class="mt-3 text-sm" style="color: #fecaca;">{{ error }}</p>

      <p class="mt-6 text-xs" style="color: var(--color-krate-blue-200);">
        We use your email to send you beta status updates. We never sell or share it. <router-link to="/privacy" class="underline">Privacy</router-link>.
      </p>
    </div>
  </section>
</template>

<style scoped>
.waitlist-input {
  background-color: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.45);
  color: #ffffff;
}
.waitlist-input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}
.waitlist-input:focus {
  background-color: rgba(255, 255, 255, 0.18);
  border-color: rgba(255, 255, 255, 0.85);
}
</style>
